import React from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@grofers/dock-design-kit'
import FullPageLoader from '../FullPageLoader'
import { BRAND_ACCESS_GUARD_TEXT } from '@/constants/commonConstants'
import { isBrandAccessDenied } from '@/utils/helper.utils'

export interface BrandAccessGuardProps {
  isLoading: boolean
  isReady: boolean
  error: unknown
  homeRoute: string
  onRetry: () => void
  children: React.ReactNode
}

// Renders a brand page only once its brand has loaded. The server decides access (internal users
// can open any brand, others only their own), so a refusal shows a no-access page here.
const BrandAccessGuard: React.FC<BrandAccessGuardProps> = ({
  isLoading,
  isReady,
  error,
  homeRoute,
  onRetry,
  children,
}) => {
  const navigate = useNavigate()

  if (error) {
    const isDenied = isBrandAccessDenied(error)
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-2 bg-white px-4 text-center">
        <p className="text-bodyLg font-bold text-gray-900">
          {isDenied ? BRAND_ACCESS_GUARD_TEXT.DENIED_TITLE : BRAND_ACCESS_GUARD_TEXT.ERROR_TITLE}
        </p>
        <p className="max-w-md text-bodySm text-gray-500">
          {isDenied
            ? BRAND_ACCESS_GUARD_TEXT.DENIED_DESCRIPTION
            : BRAND_ACCESS_GUARD_TEXT.ERROR_DESCRIPTION}
        </p>
        <div className="mt-4 flex gap-3">
          {!isDenied && (
            <Button variant="secondary" size="medium" onClick={onRetry}>
              {BRAND_ACCESS_GUARD_TEXT.RETRY_LABEL}
            </Button>
          )}
          <Button variant="primary" size="medium" onClick={() => navigate(homeRoute)}>
            {BRAND_ACCESS_GUARD_TEXT.HOME_LABEL}
          </Button>
        </div>
      </div>
    )
  }

  if (isLoading || !isReady) return <FullPageLoader className="h-screen w-full" />

  return <>{children}</>
}

export default BrandAccessGuard
